import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { HandoffRow } from '../../core/api/api.types';
import { HandoffApiService } from '../../core/api/handoff-api.service';
import { ReferenceApiService } from '../../core/api/reference-api.service';
import { HandoffSummaryCardComponent } from '../../shared/handoff-summary-card/handoff-summary-card.component';

@Component({
  selector: 'op-handoff-summary-page',
  imports: [
    RouterModule,
    DatePipe,
    MatIconModule,
    MatProgressSpinnerModule,
    HandoffSummaryCardComponent,
  ],
  templateUrl: './handoff-summary.page.html',
  styleUrl: './handoff-summary.page.scss',
})
export class HandoffSummaryPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(HandoffApiService);
  private readonly referenceApi = inject(ReferenceApiService);

  readonly handoff = signal<HandoffRow | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly claiming = signal(false);

  private categoryNames = new Map<string, string>();

  async ngOnInit(): Promise<void> {
    const handoffId = this.route.snapshot.paramMap.get('handoffId');
    if (!handoffId) {
      this.error.set('Missing handoff id.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      const [resp, categoriesRes] = await Promise.all([
        this.api.getById(handoffId),
        this.referenceApi.categories().catch(() => ({ items: [] })),
      ]);

      for (const cat of categoriesRes.items) {
        this.categoryNames.set(cat.category_id, cat.name);
      }

      if (!resp.handoff) {
        this.error.set('Handoff not found.');
        return;
      }
      this.handoff.set(resp.handoff);
    } catch (err: any) {
      console.error(err);
      this.error.set(err?.message ?? 'Failed to load handoff summary.');
    } finally {
      this.loading.set(false);
    }
  }

  customerLabel(row: HandoffRow): string {
    if (row.customer_preferred_name) return row.customer_preferred_name;
    const source = (row.conversation_id || row.id || '').replace(/-/g, '').toUpperCase();
    const suffix = source.slice(-4);
    return suffix ? `Customer #${suffix}` : 'Customer';
  }

  formatCreatedAt(value: string): string {
    const createdAt = new Date(value);
    if (Number.isNaN(createdAt.getTime())) return '—';

    const diffMs = Math.max(0, Date.now() - createdAt.getTime());
    const totalMinutes = Math.floor(diffMs / 60000);

    if (totalMinutes < 1) return 'just now';
    if (totalMinutes < 60) return `${totalMinutes}m ago`;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours < 24) {
      if (minutes > 0) return `${hours}h ${minutes}m ago`;
      return `${hours}h ago`;
    }

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  async claim(row: HandoffRow): Promise<void> {
    if (this.claiming()) return;
    this.claiming.set(true);
    this.error.set(null);
    try {
      await this.api.take(row.id);
      await this.router.navigate(['/my-cases', row.id]);
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to claim this case. Please try again.');
      this.claiming.set(false);
    }
  }

  reasonLabel(reason: string | null | undefined): string {
    if (!reason) return '—';
    return reason
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  selectedCategory(row: HandoffRow): string | null {
    const id = row.suggested_category_id ?? row.ai_summary?.categoryId ?? null;
    if (!id) return null;
    return this.categoryNames.get(id) ?? id;
  }
}